import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog, StatusBadge, toneForStatus } from '@ibe/ui';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { PropertySelect } from '../../../components/form/property-select.js';
import { PrimaryCell, RowActions } from '../../../components/resource-list/cells.js';
import { ResourceListPage } from '../../../components/resource-list/resource-list-page.js';
import { type ColumnDescriptor } from '../../../lib/resource-list/columns.js';
import { taxes, taxEndpoints } from '../api/endpoints.js';
import { taxRow, type TaxRow } from '../api/schemas.js';

function formatDate(value: string | undefined): string {
  if (value === undefined) return '';
  return new Date(value).toLocaleDateString('en-AU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatValue(row: TaxRow): string {
  if (row.rate !== undefined && row.rate !== null) return `${row.rate}%`;
  if (row.amount !== undefined && row.amount !== null && row.currency !== undefined) {
    return `${row.amount} ${row.currency}`;
  }
  return '';
}

const columns: readonly ColumnDescriptor<TaxRow>[] = [
  {
    id: 'name',
    header: 'Tax',
    sortable: true,
    alwaysVisible: true,
    fields: ['name', 'type'],
    cell: (row) => (
      <PrimaryCell
        label={row.name ?? row.id}
        to={`/taxes/${row.id}`}
        secondary={row.type ?? null}
      />
    ),
  },
  {
    id: 'basis',
    header: 'Basis',
    sortable: true,
    fields: ['basis'],
    cell: (row) => row.basis,
  },
  {
    id: 'value',
    header: 'Value',
    sortable: false,
    fields: ['rate', 'amount', 'currency'],
    cell: (row) => formatValue(row),
  },
  {
    id: 'priority',
    header: 'Priority',
    sortable: true,
    fields: ['priority'],
    cell: (row) => row.priority,
  },
  {
    id: 'enabled',
    header: 'Status',
    sortable: true,
    fields: ['enabled'],
    cell: (row) => (
      <StatusBadge tone={toneForStatus(row.enabled ? 'ACTIVE' : 'INACTIVE')}>
        {row.enabled ? 'Active' : 'Inactive'}
      </StatusBadge>
    ),
  },
  {
    id: 'createdAt',
    header: 'Created',
    sortable: true,
    fields: ['createdAt'],
    defaultVisible: false,
    cell: (row) => formatDate(row.createdAt),
  },
  {
    id: 'actions',
    header: '',
    headerLabel: 'Actions',
    align: 'right',
    alwaysVisible: true,
    fields: ['name'],
    cell: (row: TaxRow): React.JSX.Element => <TaxRowActions row={row} />,
  },
];

function TaxRowActions({ row }: { readonly row: TaxRow }): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const softDelete = taxes.useSoftDelete();
  const label = row.name ?? row.id;

  return (
    <>
      <div className="flex flex-nowrap items-center justify-end gap-1">
        <RowActions
          actions={[
            { label: `View ${label}`, icon: Eye, to: `/taxes/${row.id}` },
            { label: `Edit ${label}`, icon: Pencil, to: `/taxes/${row.id}/edit` },
          ]}
        />
        <PermissionGuard permission={PERMISSIONS.TAX_DELETE}>
          <RowActions
            actions={[
              {
                label: `Delete ${label}`,
                icon: Trash2,
                destructive: true,
                onClick: () => {
                  setConfirming(true);
                },
              },
            ]}
          />
        </PermissionGuard>
      </div>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Delete tax"
        description={`${label} will be disabled and hidden from future quotes.`}
        confirmLabel="Delete"
        destructive
        loading={softDelete.isPending}
        onConfirm={() => {
          softDelete.mutate(row.id, {
            onSuccess: () => {
              setConfirming(false);
            },
          });
        }}
      />
    </>
  );
}

function PropertyFilter(): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const propertyId = params.get('propertyId') ?? '';

  return (
    <PropertySelect
      value={propertyId}
      onChange={(next) => {
        setParams((prev) => {
          const nextParams = new URLSearchParams(prev);
          if (next === '') {
            nextParams.delete('propertyId');
          } else {
            nextParams.set('propertyId', next);
          }
          nextParams.delete('page');
          return nextParams;
        });
      }}
      hideDescription
    />
  );
}

export function TaxListPage(): React.JSX.Element {
  const [params] = useSearchParams();
  const propertyId = params.get('propertyId') ?? '';
  const scope = propertyId === '' ? {} : { propertyId };

  return (
    <ResourceListPage
      title="Taxes"
      description="The taxes and service charges applied to bookings."
      path={taxEndpoints.list}
      resource="taxes"
      schema={taxRow}
      columns={columns}
      rowKey={(row) => row.id}
      permission={PERMISSIONS.TAX_READ}
      scope={scope}
      filterPrefix={<PropertyFilter />}
      actions={
        <PermissionGuard permission={PERMISSIONS.TAX_CREATE}>
          <Button asChild>
            <Link to="/taxes/new">
              <Plus className="h-4 w-4" aria-hidden="true" />
              New tax
            </Link>
          </Button>
        </PermissionGuard>
      }
    />
  );
}
